import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Command, Loader2, ArrowLeft, Check } from "lucide-react";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ loading: true, valid: false, reason: "", type: "", email: "" });
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invite/${token}`)
      .then(({ data }) => {
        setState({ loading: false, valid: data.valid, reason: data.reason || "", type: data.type || "", email: data.email || "" });
        if (data.valid && data.email) setForm((f) => ({ ...f, email: data.email }));
      })
      .catch(() => setState({ loading: false, valid: false, reason: "This invite link is not valid.", type: "", email: "" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await api.post(`/invite/${token}/accept`, form);
      await refresh();
      navigate("/candidate", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white text-[#09090B]">
      <div className="hidden lg:flex flex-col justify-between p-14 border-r border-black/10 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <Link to="/" className="relative flex items-center gap-2 text-muted-foreground hover:text-[#09090B] transition-colors text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <div className="relative">
          <div className="h-10 w-10 bg-primary flex items-center justify-center mb-8"><Command className="h-5 w-5" /></div>
          <p className="overline mb-3">A personal invitation</p>
          <h1 className="font-heading font-black text-5xl tracking-tight leading-[0.95]">You've been<br />invited.</h1>
          <p className="text-muted-foreground mt-6 max-w-sm">
            A recruiter at Innovation Talent Partners has invited you to join our private candidate network — where we place leaders across executive, platform, operational, venture, board and fractional roles in the innovation economy.
          </p>
          <ul className="mt-8 space-y-3 max-w-sm">
            {[
              "Get matched to senior roles that fit your background and ambitions",
              "Be introduced directly by a recruiter — not lost in an application pile",
              "Signal interest in board, fractional and advisory work",
              "Control your visibility: stay searchable or keep your profile confidential",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-muted-foreground">
                <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={2.5} /> {t}
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground/80 mt-8 max-w-sm">
            Your profile is private to our recruiting team. We never share your details with a company without your say-so.
          </p>
        </div>
        <p className="relative overline">Innovation Talent Partners</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-14">
        <div className="w-full max-w-sm">
          {state.loading ? (
            <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : !state.valid ? (
            <div data-testid="invite-invalid" className="text-center">
              <h2 className="font-heading font-black text-2xl">Invite unavailable</h2>
              <p className="text-sm text-muted-foreground mt-3">{state.reason}</p>
              <Link to="/login" className="inline-block mt-6 text-sm text-primary hover:underline">Go to sign in</Link>
            </div>
          ) : (
            <>
              <h2 className="font-heading font-black text-2xl mb-1">Create your account</h2>
              <p className="text-sm text-muted-foreground mb-8">
                It takes under a minute. Once you're in, you can complete your profile, add a resume and headshot, and set your role preferences.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full name</Label>
                  <Input id="full_name" data-testid="invite-fullname" value={form.full_name} onChange={set("full_name")} required className="bg-[#F4F4F5] border-black/10 rounded-none h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" data-testid="invite-accept-email" value={form.email} onChange={set("email")} required readOnly={state.type === "single" && !!state.email}
                    className={`border-black/10 rounded-none h-11 ${state.type === "single" && state.email ? "bg-black/5 text-muted-foreground" : "bg-[#F4F4F5]"}`} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" data-testid="invite-password" value={form.password} onChange={set("password")} required className="bg-[#F4F4F5] border-black/10 rounded-none h-11" />
                </div>
                {error && <p data-testid="invite-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 px-3 py-2">{error}</p>}
                <Button type="submit" data-testid="invite-submit" disabled={submitting} className="w-full h-11 rounded-none bg-primary hover:bg-[#09090B] hover:text-white transition-colors font-medium">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join the portal"}
                </Button>
                <p className="text-xs text-muted-foreground text-center">By joining you agree to be considered for relevant roles. No spam — just opportunities that match.</p>
              </form>
              <Link to="/login" className="block text-center text-sm text-muted-foreground hover:text-[#09090B] transition-colors mt-4">Already have an account? Sign in</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
